import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import ListingNavigation from '../../components/originals/navigation/ListingNavigation'
import PropertyCard from '../../components/originals/PropertyCard'
import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../types/Property/Property'
import PropertyService from '../../services/property.service'
import { extractFilters } from '../../lib/utils/extractFilters'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import useLocale from '../../hooks/useLocale'
import { HeroIcons } from '../../lib/utils/heroIcons'
import Layout from '../../components/Layout'
import { GoogleTagManagerService } from '../../services/google-tag-manager.service'
import { mapContent, mapItem } from '../../lib/utils/helperGtm'

function ListingPage() {
  const { t } = useTranslation('originals/listing')
  const { t: t2 } = useTranslation('common')
  const { locale } = useLocale()

  const [properties, setProperties] = useState<PropertyWithAccess[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [maxPages, setMaxPages] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<any>({
    orderBy: 'createdAt',
    order: 'DESC',
  })
  const [filtersObj, setFiltersObj] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)

  const fetchProperties = async () => {
    setIsLoading(true)
    setLoadingError(null)

    const filters = extractFilters(filtersObj)

    try {
      const resProperties = await PropertyService.getProperties({
        page: currentPage,
        pageSize: 10,
        lang: locale,
        order: orderBy.order,
        orderBy: orderBy.orderBy,
        filter: filters.filter,
        filterBy: filters.filterBy,
        filterOperator: filters.filterOperator,
      })
      setProperties(resProperties.properties as PropertyWithAccess[])
      setMaxPages(resProperties.maxPages)

      GoogleTagManagerService.ViewItemList({
        items: resProperties.properties.map((item) => mapItem(item, 1)),
        contents: resProperties.properties.map((item) => mapContent(item, 1)),
      })
    } catch (e) {
      setLoadingError(t2('loading.error'))
    }
    setIsLoading(false)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleOrderByChange = (orderBy: any) => {
    setOrderBy(orderBy.value)
  }

  const handleOnChangeFilters = (filters: any[]) => {
    if (JSON.stringify(filters) === JSON.stringify(filtersObj)) return
    setFiltersObj(filters)
  }

  const handlePropertyClick = (
    property: Property | MaskedProperty,
    itemListId: string,
    itemListName: string,
  ) => {
    GoogleTagManagerService.ClickItem({
      itemListId,
      itemListName,
      item: mapItem(property),
    })
  }

  const displayProperties = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center py-24">
          <div className="mx-auto flex items-center">
            {t2('loading.inProgress')}
            <HeroIcons.ArrowPathIcon className="ml-2 h-4 w-4 animate-spin" />
          </div>
        </div>
      )
    }

    if ((!properties || !properties.length) && !loadingError) {
      return (
        <div className="flex h-full items-center py-24">
          <div className="mx-auto flex items-center text-center">
            {t2('loading.emptyResults')}
          </div>
        </div>
      )
    }

    if (loadingError) {
      return (
        <div className="flex h-full items-center py-24">
          <div className="mx-auto flex items-center text-center">
            {t2('loading.error')}
          </div>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4">
        {properties.map((property: PropertyWithAccess) => (
          <PropertyCard
            key={property.id}
            property={property}
            onClick={(property) =>
              handlePropertyClick(
                property,
                'search_result',
                'Liste des propriétés',
              )
            }
          />
        ))}
      </div>
    )
  }

  useEffect(() => {
    fetchProperties()
  }, [locale, currentPage, orderBy, filtersObj])

  return (
    <>
      <>
        <NextSeo
          title={t('seo.title') || ''}
          description={t('seo.description') || ''}
        />

        <div className="space-y-3">
          <ListingNavigation
            filtersObj={filtersObj}
            currentPage={currentPage}
            maxPages={maxPages}
            onPageChange={handlePageChange}
            onOrderByChange={handleOrderByChange}
            onChangeFilters={handleOnChangeFilters}
            onPropertyClick={(property) =>
              handlePropertyClick(property, 'search_bar', 'Barre de recherche')
            }
          />
        </div>
        <div>{displayProperties()}</div>
      </>
    </>
  )
}

ListingPage.getLayout = (page: ReactElement) => {
  return <Layout>{page}</Layout>
}

export default ListingPage

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'originals/global',
        'originals/listing',
      ])),
    },
  }
}
